import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import Tooltip from '@atlaskit/tooltip'
import TextField from '@atlaskit/textfield'
import TextArea from '@atlaskit/textarea'
import { updateReleaseAsync } from '../../store/slices/releaseSlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * EDIT RELEASE MODAL
 *
 * Modal for editing an existing release's name and description.
 * Features:
 * - Pre-filled with current release data
 * - Name field (required)
 * - Description field (optional)
 * - Save/Cancel buttons
 */
function EditReleaseModal({ isOpen, onClose, release }) {
  const dispatch = useDispatch()
  const { showError, showWarning } = useNotification()

  // Form state - initialized with release data
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update form when release changes or modal opens
  useEffect(() => {
    if (release && isOpen) {
      setName(release.name || '')
      setDescription(release.description || '')
    }
  }, [release, isOpen])

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarning('Validation Error', 'Please enter a release name')
      return
    }

    setIsSubmitting(true)

    try {
      await dispatch(updateReleaseAsync({
        releaseId: release.id,
        updates: {
          name: name.trim(),
          description: description.trim()
        }
      })).unwrap()

      // Close modal on success
      onClose()
    } catch (error) {
      console.error('Failed to update release:', error)
      const errorMessage = error?.message || 'Failed to update release. Please try again.'
      showError('Update Release Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle modal close - reset form to original values
  const handleClose = () => {
    setName(release?.name || '')
    setDescription(release?.description || '')
    onClose()
  }

  // Don't render if no release provided
  if (!release) {
    return null
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '480px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            {/* Left: Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
              <Dialog.Title
                style={{
                  fontSize: '20px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  margin: 0
                }}
              >
                Edit Release
              </Dialog.Title>
            </div>

            {/* Right: Close button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <Tooltip content="Close">
                <Button
                  appearance="subtle"
                  iconBefore={<CrossIcon label="Close" size="medium" />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            {/* Release Name */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Release Name *
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Release 2.1"
                isRequired
                autoFocus
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '8px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Description (optional)
              </label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Sprint 25 - Major feature release"
                minimumRows={2}
                maxHeight="100px"
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!name.trim()}
            >
              Save Changes
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { EditReleaseModal }
